/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * DynamicTable Component
 * --------
 * A reusable table component for displaying dynamic tabular data with flexible schema and pagination.
 *
 * Dual Layout:
 *  - Desktop (windowWidth > 991px): Renders a traditional table with sticky Action/Select column.
 *  - Mobile/Tablet (windowWidth ≤ 991px): Renders stacked cards, one per record.
 */

import React, { useEffect, useState } from "react";
import { useTheme } from "../../theme/ThemeContext";
import { convertUTCStringToLocalStandard, toDisplayString } from "../../utils/utils";
import JsonCell from "../common/JsonCell";
import ImageComponent from "../common/ImageComponent";
import { FILE_TYPE_MAP } from "../common/FileUploadInput";

// --- Helpers for File Type Icons ---
const getIconForCategory = (category) => {
  const map = {
    image: "bi-file-earmark-image",
    video: "bi-file-earmark-play",
    audio: "bi-file-earmark-music",
    text: "bi-file-earmark-text",
    document: "bi-file-earmark-richtext",
    archive: "bi-file-earmark-zip",
    other: "bi-file-earmark"
  };
  return map[category] || map.other;
};

const getFileMeta = (fileName) => {
  if (!fileName) return { category: "other", icon: "bi-file-earmark" };
  const ext = "." + fileName.split(".").pop().toLowerCase();

  // 1. Check strict lists from MAP
  for (const [type, config] of Object.entries(FILE_TYPE_MAP)) {
    if (config.accept && config.accept !== "*" && !config.accept.includes("/*")) {
      if (config.accept.split(",").includes(ext)) {
        return { category: type, icon: getIconForCategory(type) };
      }
    }
  }

  // 2. Fallbacks for media types where MAP uses wildcards
  if ([".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg", ".bmp", ".ico"].includes(ext)) return { category: "image", icon: "bi-file-earmark-image" };
  if ([".mp4", ".mov", ".avi", ".mkv", ".webm"].includes(ext)) return { category: "video", icon: "bi-file-earmark-play" };
  if ([".mp3", ".wav", ".ogg", ".m4a"].includes(ext)) return { category: "audio", icon: "bi-file-earmark-music" };

  return { category: "other", icon: "bi-file-earmark" };
};

const getFileNameFromUrl = (fileUrl) => {
  if (!fileUrl) return "";
  try {
    const urlObj = new URL(fileUrl, "http://dummy");
    const queryName = urlObj.searchParams.get("filename");
    if (queryName) return queryName;
  } catch (e) { /* ignore */ }

  try {
    let fileName = decodeURIComponent(fileUrl.substring(fileUrl.lastIndexOf("/") + 1));
    if (fileName.includes("?")) fileName = fileName.split("?")[0];
    return fileName;
  } catch (e) {
    return fileUrl;
  }
};

export default function DynamicTable({
  tableSchema,
  records,
  page,
  pageSize,
  onView,
  onEdit,
  onDelete,
  mode = "action",
  onSelect,
  onHistory,
  forceStacked = false,
}) {
  const { theme } = useTheme();

  /**
   * Responsive state:
   * - Track window width and derive isMobileLike: true when width ≤ 991px.
   * - Hooks MUST be called before any early return.
   */
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    function handleResize() {
      const w = window.innerWidth;
      setWindowWidth(w);
      // console.log("DynamicTable window width:", w);
    }

    // Initial measurement
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobileLike = windowWidth <= 991 || forceStacked;

  // Basic guard for missing data (AFTER hooks)
  if (!tableSchema || !tableSchema.fields || !records) {
    return <div>No data to display</div>;
  }

  // Sticky Action/Select column style (desktop table)
  const fixedActionStyle = {
    position: "sticky",
    right: 0,
    background: "#fafafa",
    zIndex: 10,
    minWidth: 80,
    textAlign: "center",
    boxShadow: "-2px 0px 5px rgba(0, 0, 0, 0.1)",
  };

  /**
   * Normalize a cell value:
   *  - Handle reference objects with .data & record_identifier
   *  - Handle datetime conversion
   *  - Fallbacks for null/empty
   */
  const getNormalizedValue = (record, field) => {
    const { name, data_type } = field;
    let value = record[name];

    if (value && typeof value === "object" && value.data) {
      const identifier = value.record_identifier || "id";
      value = value.data[identifier] || value.data.id || "-";
    }

    if (value === null || value === undefined || value === "") {
      value = "-";
    }

    if (
      data_type === "datetime" &&
      typeof value === "string" &&
      value !== "-"
    ) {
      value = convertUTCStringToLocalStandard(value);
    }

    return value;
  };

  const getFieldLabel = (field) => field.display_name || field.name;

  const formatCardValue = (val) => {
    if (val === null || val === undefined || val === "" || val === "-")
      return "—";
    if (typeof val === "boolean") return val ? "True" : "False";
    if (Array.isArray(val)) return val.join(", ");
    if (typeof val === "object") return JSON.stringify(val);
    return String(val);
  };

  return (
    <>
      {/* DESKTOP / LARGE SCREENS: Table layout */}
      {!isMobileLike && (
        <div
          className="table-responsive rounded shadow-sm"
          style={{
            border: `1.2px solid ${theme.primary}`,
            background: "#fff",
            overflowX: "auto",
            whiteSpace: "nowrap",
            maxWidth: "100%",
            padding: 0,
          }}
        >
          <table
            className="table table-striped mb-0 align-middle"
            style={{ minWidth: 600 }}
          >
            <thead>
              <tr
                style={{
                  fontWeight: 600,
                  color: theme.primary,
                  background: "#f7f7fa",
                  fontSize: 16,
                }}
              >
                {records.length > 0 && (
                  <th style={{ minWidth: 60, textAlign: "left" }}>S.No</th>
                )}
                {tableSchema.fields.map((field) => (
                  <th
                    key={field.name}
                    style={{
                      minWidth: 100,
                      textAlign:
                        field.data_type === "integer" ||
                          field.data_type === "float"
                          ? "right"
                          : "left",
                    }}
                  >
                    {getFieldLabel(field)}
                  </th>
                ))}
                {mode === "action" && records.length > 0 && (
                  <th style={fixedActionStyle}>Action</th>
                )}
                {mode === "select" && records.length > 0 && (
                  <th style={fixedActionStyle}>Select</th>
                )}
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td
                    colSpan={tableSchema.fields.length + 2}
                    style={{
                      textAlign: "center",
                      padding: "28px 0",
                      color: "#aaa",
                      fontStyle: "italic",
                    }}
                  >
                    No records found.
                  </td>
                </tr>
              ) : (
                records.map((record, idx) => (
                  <tr key={record.id || idx}>
                    <td style={{ textAlign: "left" }}>
                      {(page - 1) * pageSize + idx + 1}
                    </td>
                    {tableSchema.fields.map((field) => {
                      const value = getNormalizedValue(record, field);

                      if (
                        (field.data_type || "").toLowerCase() === "image"
                      ) {
                        const imageUrl =
                          value && value !== "-" ? value.toString() : null;
                        return (
                          <td
                            key={`${record.id || idx}-${field.name}`}
                            style={{
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <ImageComponent
                              src={imageUrl}
                              alt={getFieldLabel(field)}
                              height={75}
                              shape="square"
                            />
                          </td>
                        );
                      }

                      // --- New FILE Type Handling ---
                      if ((field.data_type || "").toLowerCase() === "file") {
                        let fileUrl = value && value !== "-" ? value.toString() : null;
                        if (!fileUrl) {
                          return <td key={`${record.id || idx}-${field.name}`}>-</td>;
                        }

                        // Browsers cannot natively send Authorization Bearer headers via <a> tags.
                        // Append it to the query string so Django's OAuth Toolkit parses it.
                        if (fileUrl.includes("/application/file/download/")) {
                            try {
                                const authData = JSON.parse(localStorage.getItem("access_token") || "{}");
                                if (authData.accessToken) {
                                    fileUrl += (fileUrl.includes("?") ? "&" : "?") + `access_token=${authData.accessToken}`;
                                }
                            } catch (e) {}
                        }

                        const fileName = getFileNameFromUrl(fileUrl);
                        const { icon } = getFileMeta(fileName);

                        return (
                          <td
                            key={`${record.id || idx}-${field.name}`}
                            style={{ textAlign: "left", verticalAlign: "middle" }}
                          >
                            <a
                              href={fileUrl}
                              download={fileName}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Click to download"
                              style={{
                                textDecoration: "none",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                cursor: "pointer",
                                width: "100%",
                                color: "inherit"
                              }}
                            >
                              <i
                                className={`bi ${icon}`}
                                style={{
                                  fontSize: "1.4em",
                                  color: theme.primary,
                                  lineHeight: 1
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "0.9em",
                                  maxWidth: "180px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  color: "#444",
                                  textDecoration: "underline"
                                }}
                                title={fileName}
                              >
                                {fileName}
                              </span>
                              <i
                                className="bi bi-download"
                                style={{
                                  fontSize: "1.1em",
                                  color: theme.primary
                                }}
                              />
                            </a>
                          </td>
                        );
                      }

                      if (field.data_type === "geo_point") {
                        const geo = record[field.name];
                        if (!geo || geo === "-") {
                          return <td key={`${record.id || idx}-${field.name}`}>-</td>;
                        }
                        const lat = geo.lat ?? geo.latitude ?? "";
                        const lng = geo.lng ?? geo.longitude ?? "";
                        return (
                          <td key={`${record.id || idx}-${field.name}`}>
                            {`${lat}, ${lng}`}
                          </td>
                        );
                      }

                      if (field.data_type === "json") {
                        const jsonVal = record[field.name];
                        if (jsonVal === null || jsonVal === undefined || jsonVal === "-") {
                          return <td key={`${record.id || idx}-${field.name}`}>-</td>;
                        }
                        return (
                          <td key={`${record.id || idx}-${field.name}`}>
                            <JsonCell value={jsonVal} />
                          </td>
                        );
                      }

                      if (field.data_type === "uuid") {
                        const uuidVal = record[field.name];
                        return (
                          <td key={`${record.id || idx}-${field.name}`}>
                            <span style={{ fontFamily: "monospace", fontSize: "0.82rem", color: "#444" }}>
                              {uuidVal || "-"}
                            </span>
                          </td>
                        );
                      }

                      const alignRight =
                        field.data_type === "integer" ||
                        field.data_type === "float";

                      const dataType = (field.data_type || "").toLowerCase();
                      const isText = dataType === "text" || dataType === "string";

                      let cellContent = React.isValidElement(value)
                        ? value
                        : value !== null && value !== undefined
                          ? toDisplayString(value)
                          : "-";

                      let uiRules = field.ui_rules || {};
                      if (typeof uiRules === 'string') {
                        try { uiRules = JSON.parse(uiRules); } catch (e) { uiRules = {}; }
                      }
                      const badgeSettings = uiRules?.display_as_badge;
                      console.log("Column UI Rules for", field.name, uiRules); const isBadge = badgeSettings?.enabled === true && cellContent !== "-";

                      if (isBadge) {
                        cellContent = (
                          <span
                            style={{
                              backgroundColor: badgeSettings.color || "#0d6efd",
                              color: "#fff",
                              padding: "4px 8px",
                              borderRadius: "12px",
                              fontSize: "0.85em",
                              fontWeight: 500,
                              display: "inline-block",
                            }}
                          >
                            {cellContent}
                          </span>
                        );
                      }

                      return (
                        <td
                          key={`${record.id || idx}-${field.name}`}
                          style={{ textAlign: alignRight ? "right" : "left" }}
                        >
                          {isText && !isBadge ? (
                            <div
                              style={{
                                maxWidth: "360px",
                                whiteSpace: "pre-wrap",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                              title={
                                typeof cellContent === "string"
                                  ? cellContent
                                  : undefined
                              }
                            >
                              {cellContent}
                            </div>
                          ) : (
                            cellContent
                          )}
                        </td>
                      );
                    })}
                    {mode === "action" && (
                      <td style={fixedActionStyle}>
                        <button
                          onClick={() => onView && onView(record)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            marginRight: 8,
                            color: theme.primary,
                            fontSize: 18,
                          }}
                          aria-label="View"
                          title="View"
                        >
                          <i className="bi bi-eye" />
                        </button>
                        {onEdit && (
                          <button
                            onClick={() => onEdit(record)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              marginRight: 8,
                              color: theme.primary,
                              fontSize: 18,
                            }}
                            aria-label="Edit"
                            title="Edit"
                          >
                            <i className="bi bi-pencil" />
                          </button>
                        )}

                        {onHistory && (
                          <button
                            onClick={() => onHistory(record)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              marginRight: 8,
                              color: theme.primary,
                              fontSize: 18,
                            }}
                            aria-label="History"
                            title="View History"
                          >
                            <i className="bi bi-clock-history" />
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() => onDelete(record)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: theme.primary,
                              fontSize: 18,
                            }}
                            aria-label="Delete"
                            title="Delete"
                          >
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </td>
                    )}
                    {mode === "select" && (
                      <td style={fixedActionStyle}>
                        <button
                          onClick={() =>
                            onSelect &&
                            onSelect({
                              record,
                              recordIdentifier:
                                tableSchema.record_identifier,
                            })
                          }
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: theme.primary,
                            fontSize: 20,
                          }}
                          aria-label="Select"
                          title="Select"
                        >
                          <i className="bi bi-check2-circle" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div >
      )
      }

      {/* MOBILE / TABLET: Stacked card layout */}
      {
        isMobileLike && (
          <div
            style={{
              border: `1.2px solid ${theme.primary}`,
              background: "#fff",
              borderRadius: 6,
              padding: 8,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            {records.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "28px 0",
                  color: "#aaa",
                  fontStyle: "italic",
                }}
              >
                No records found.
              </div>
            ) : (
              records.map((record, idx) => (
                <div
                  key={record.id || idx}
                  style={{
                    border: "1px solid #e4e6f0",
                    borderRadius: 10,
                    padding: "10px 12px",
                    marginBottom: 10,
                    background: "#fff",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.04)",
                  }}
                >
                  {/* Header: S.No + actions/select */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 6,
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        color: "#6c6f85",
                        fontWeight: 600,
                      }}
                    >
                      S.No:&nbsp;
                      <span style={{ color: theme.primary }}>
                        {(page - 1) * pageSize + idx + 1}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      {mode === "action" && (
                        <>
                          <button
                            onClick={() => onView && onView(record)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: theme.primary,
                              padding: 4,
                            }}
                            aria-label="View"
                            title="View"
                          >
                            <i className="bi bi-eye" />
                          </button>
                          {onEdit && (
                            <button
                              onClick={() => onEdit(record)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: theme.primary,
                                padding: 4,
                              }}
                              aria-label="Edit"
                              title="Edit"
                            >
                              <i className="bi bi-pencil" />
                            </button>
                          )}
                          {onHistory && (
                            <button
                              onClick={() => onHistory(record)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: theme.primary,
                                padding: 4,
                              }}
                              aria-label="History"
                              title="View History"
                            >
                              <i className="bi bi-clock-history" />
                            </button>
                          )}
                          {onDelete && (
                            <button
                              onClick={() => onDelete(record)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: "#d53b3b",
                                padding: 4,
                              }}
                              aria-label="Delete"
                              title="Delete"
                            >
                              <i className="bi bi-trash" />
                            </button>
                          )}
                        </>
                      )}

                      {mode === "select" && (
                        <button
                          onClick={() =>
                            onSelect &&
                            onSelect({
                              record,
                              recordIdentifier: tableSchema.record_identifier,
                            })
                          }
                          style={{
                            borderRadius: theme.buttonRadius,
                            border: `1px solid ${theme.primary}`,
                            background: "#fff",
                            color: theme.primary,
                            padding: "4px 10px",
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: "pointer",
                          }}
                          aria-label="Select"
                          title="Select"
                        >
                          Select
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Body: fields stacked */}
                  <div>
                    {tableSchema.fields.map((field) => {
                      const rawValue = getNormalizedValue(record, field);

                      if ((field.data_type || "").toLowerCase() === "image") {
                        const imageUrl =
                          rawValue && rawValue !== "-" ? rawValue.toString() : null;
                        return (
                          <div
                            key={`${record.id || idx}-${field.name}`}
                            style={{
                              marginBottom: 8,
                              paddingBottom: 8,
                              borderBottom: "1px dashed #eef0f5",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 11,
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                color: "#9aa0b5",
                                marginBottom: 2,
                              }}
                            >
                              {getFieldLabel(field)}
                            </div>
                            <div style={{ textAlign: "left" }}>
                              <ImageComponent
                                src={imageUrl}
                                alt={getFieldLabel(field)}
                                height={75}
                                shape="square"
                              />
                            </div>
                          </div>
                        );
                      }

                      // --- New FILE Type Handling (Mobile) ---
                      if ((field.data_type || "").toLowerCase() === "file") {
                        let fileUrl = rawValue && rawValue !== "-" ? rawValue.toString() : null;

                        if (fileUrl && fileUrl.includes("/application/file/download/")) {
                            try {
                                const authData = JSON.parse(localStorage.getItem("access_token") || "{}");
                                if (authData.accessToken) {
                                    fileUrl += (fileUrl.includes("?") ? "&" : "?") + `access_token=${authData.accessToken}`;
                                }
                            } catch (e) {}
                        }

                        const fileName = fileUrl ? getFileNameFromUrl(fileUrl) : "-";
                        const { icon } = getFileMeta(fileName);

                        return (
                          <div
                            key={`${record.id || idx}-${field.name}`}
                            style={{
                              marginBottom: 10,
                              paddingBottom: 10,
                              borderBottom: "1px dashed #eef0f5",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 11,
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                color: "#9aa0b5",
                                marginBottom: 4,
                              }}
                            >
                              {getFieldLabel(field)}
                            </div>
                            <div style={{ textAlign: "left" }}>
                              {!fileUrl ? (
                                "-"
                              ) : (
                                <a
                                  href={fileUrl}
                                  download={fileName}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Click to download"
                                  style={{
                                    textDecoration: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    cursor: "pointer",
                                    color: "inherit"
                                  }}
                                >
                                  <i
                                    className={`bi ${icon}`}
                                    style={{
                                      fontSize: "1.5em",
                                      color: theme.primary,
                                      lineHeight: 1
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: "0.95em",
                                      wordBreak: "break-all",
                                      color: "#333",
                                      textDecoration: "underline"
                                    }}
                                  >
                                    {fileName}
                                  </span>
                                  <i
                                    className="bi bi-download"
                                    style={{
                                      fontSize: "1.2em",
                                      color: theme.primary
                                    }}
                                  />
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={`${record.id || idx}-${field.name}`}
                          style={{
                            marginBottom: 6,
                            paddingBottom: 6,
                            borderBottom: "1px dashed #eef0f5",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                              color: "#9aa0b5",
                              marginBottom: 2,
                            }}
                          >
                            {getFieldLabel(field)}
                          </div>
                          <div
                            style={{
                              fontSize: 14,
                              color: "#444",
                              wordBreak: "break-word",
                              ...((
                                (field.data_type || "").toLowerCase() === "text" ||
                                (field.data_type || "").toLowerCase() === "string"
                              ) ? {
                                whiteSpace: "pre-wrap",
                                display: "-webkit-box",
                                WebkitLineClamp: 5,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              } : {})
                            }}
                          >
                            {(() => {
                              let uiRules = field.ui_rules || {};
                              if (typeof uiRules === 'string') {
                                try { uiRules = JSON.parse(uiRules); } catch (e) { uiRules = {}; }
                              }
                              const badgeSettings = uiRules?.display_as_badge;
                              console.log("Column UI Rules for", field.name, uiRules); const isBadge = badgeSettings?.enabled === true && rawValue !== "-" && rawValue !== null && rawValue !== undefined;

                              if (isBadge) {
                                return (
                                  <span
                                    style={{
                                      backgroundColor: badgeSettings.color || "#0d6efd",
                                      color: "#fff",
                                      padding: "4px 8px",
                                      borderRadius: "12px",
                                      fontSize: "0.85em",
                                      fontWeight: 500,
                                      display: "inline-block",
                                    }}
                                  >
                                    {React.isValidElement(rawValue) ? rawValue : formatCardValue(rawValue)}
                                  </span>
                                );
                              }
                              return React.isValidElement(rawValue) ? rawValue : formatCardValue(rawValue);
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )
      }
    </>
  );
}
